// ============================================================================
// TARGET TRACKER
// ============================================================================
// Emission reduction target management with progress tracking
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { emissionsApi } from '../api/emissions.api';
import PageInfoButton from '@/components/PageInfoButton';

interface Target {
  id: number;
  name: string;
  target_type: string;
  scope?: string;
  baseline_year: number;
  target_year: number;
  baseline_value: number;
  target_value: number;
  current_value?: number;
  intensity_metric?: string;
  status?: string;
  progress_pct?: number;
}

interface CreateTargetForm {
  name: string;
  target_type: string;
  scope: string;
  baseline_year: string;
  target_year: string;
  baseline_value: string;
  target_value: string;
  intensity_metric: string;
}

const DEFAULT_FORM: CreateTargetForm = {
  name: '',
  target_type: 'absolute',
  scope: '',
  baseline_year: '',
  target_year: '',
  baseline_value: '',
  target_value: '',
  intensity_metric: '',
};

const progressColor = (pct: number) => {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 40) return 'bg-yellow-400';
  return 'bg-red-400';
};

const statusBadge = (status?: string) => {
  const s = status ?? 'active';
  const map: Record<string, string> = {
    active: 'bg-blue-100 text-blue-700',
    achieved: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    pending: 'bg-slate-100 text-slate-600',
  };
  return map[s] ?? 'bg-slate-100 text-slate-600';
};

const TargetTracker: React.FC = () => {
  const [targets, setTargets] = useState<Target[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateTargetForm>(DEFAULT_FORM);
  const [updateId, setUpdateId] = useState<number | null>(null);
  const [currentValue, setCurrentValue] = useState('');

  const loadTargets = useCallback(async () => {
    try {
      const data = await emissionsApi.getTargets();
      setTargets(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load targets');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadTargets();
      setLoading(false);
    };
    init();
  }, [loadTargets]);

  const createTarget = async () => {
    if (!form.name.trim()) return;
    try {
      const payload: Parameters<typeof emissionsApi.createTarget>[0] = {
        name: form.name,
        target_type: form.target_type,
        baseline_year: Number(form.baseline_year),
        target_year: Number(form.target_year),
        baseline_value: Number(form.baseline_value),
        target_value: Number(form.target_value),
      };
      if (form.scope) payload.scope = form.scope;
      if (form.intensity_metric) payload.intensity_metric = form.intensity_metric;
      await emissionsApi.createTarget(payload);
      setShowCreate(false);
      setForm(DEFAULT_FORM);
      await loadTargets();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create target');
    }
  };

  const updateProgress = async () => {
    if (updateId === null) return;
    try {
      await emissionsApi.updateTargetProgress(updateId, Number(currentValue));
      setUpdateId(null);
      setCurrentValue('');
      await loadTargets();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update progress');
    }
  };

  const deleteTarget = async (id: number) => {
    try {
      await emissionsApi.deleteTarget(id);
      await loadTargets();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete target');
    }
  };

  const calcProgress = (t: Target) => {
    if (t.progress_pct !== undefined) return t.progress_pct;
    if (t.current_value === undefined) return 0;
    const reduction = t.baseline_value - t.current_value;
    const targetReduction = t.baseline_value - t.target_value;
    if (targetReduction <= 0) return 0;
    return Math.min(100, Math.round((reduction / targetReduction) * 100));
  };

  const totalTargets = targets.length;
  const activeCount = targets.filter(t => (t.status ?? 'active') === 'active').length;
  const achievedCount = targets.filter(t => t.status === 'achieved').length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Reduction Targets</h1><PageInfoButton /></div>
          <p className="text-slate-500 mt-1">Track your emission reduction commitments</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          + Create Target
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Targets', value: totalTargets, color: 'text-slate-900' },
          { label: 'Active', value: activeCount, color: 'text-blue-700' },
          { label: 'Achieved', value: achievedCount, color: 'text-green-700' },
        ].map(card => (
          <div key={card.label} className="bg-white border rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{card.label}</p>
            <p className={`text-3xl font-bold mt-1 ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Targets Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-sm font-semibold text-slate-700">All Targets</h2>
        </div>
        {targets.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">
            No targets yet. Create one to start tracking reductions.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Period</th>
                  <th className="px-4 py-3 text-left min-w-40">Progress</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {targets.map(t => {
                  const pct = calcProgress(t);
                  return (
                    <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-left font-medium text-slate-800">
                        {t.name}
                        {t.scope && <span className="ml-2 text-xs text-slate-400">{t.scope}</span>}
                      </td>
                      <td className="px-4 py-3 text-left text-slate-600 capitalize">{t.target_type}</td>
                      <td className="px-4 py-3 text-left text-slate-600">
                        {t.baseline_year} → {t.target_year}
                      </td>
                      <td className="px-4 py-3 text-left">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden min-w-24">
                            <div
                              className={`h-full ${progressColor(pct)} rounded-full transition-all`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-slate-600 w-8 text-right">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusBadge(t.status)}`}>
                          {t.status ?? 'active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setUpdateId(t.id); setCurrentValue(String(t.current_value ?? '')); }}
                            className="px-2 py-1 text-xs border border-slate-300 rounded text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            Update
                          </button>
                          <button
                            onClick={() => deleteTarget(t.id)}
                            className="px-2 py-1 text-xs border border-red-200 rounded text-red-600 hover:bg-red-50 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Target Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Create Reduction Target</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. 50% Scope 1 reduction by 2030"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Target Type</label>
                  <select
                    value={form.target_type}
                    onChange={e => setForm(f => ({ ...f, target_type: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="absolute">Absolute</option>
                    <option value="percentage">Percentage</option>
                    <option value="intensity">Intensity</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Scope</label>
                  <input
                    type="text"
                    value={form.scope}
                    onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Scope 1, 2, 3, or All"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Baseline Year *</label>
                  <input
                    type="number"
                    value={form.baseline_year}
                    onChange={e => setForm(f => ({ ...f, baseline_year: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="2020"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Target Year *</label>
                  <input
                    type="number"
                    value={form.target_year}
                    onChange={e => setForm(f => ({ ...f, target_year: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="2030"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Baseline Value *</label>
                  <input
                    type="number"
                    step="any"
                    value={form.baseline_value}
                    onChange={e => setForm(f => ({ ...f, baseline_value: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Target Value *</label>
                  <input
                    type="number"
                    step="any"
                    value={form.target_value}
                    onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="500"
                  />
                </div>
              </div>
              {form.target_type === 'intensity' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Intensity Metric</label>
                  <input
                    type="text"
                    value={form.intensity_metric}
                    onChange={e => setForm(f => ({ ...f, intensity_metric: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g. tCO2e / revenue"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowCreate(false); setForm(DEFAULT_FORM); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createTarget}
                disabled={!form.name.trim()}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                Create Target
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Progress Modal */}
      {updateId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Update Progress</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Current Value</label>
              <input
                type="number"
                step="any"
                value={currentValue}
                onChange={e => setCurrentValue(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter current emissions value"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setUpdateId(null); setCurrentValue(''); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateProgress}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetTracker;
